import {
  normalizeVelocityBcBands,
  parseVelocityBcBands,
  serializeVelocityBcBands,
} from "./velocity-bands.js";
import {
  migrateLegacyMvTemperatureTable,
  normalizeMvTemperatureTable,
  parseMvTemperatureTable,
  serializeMvTemperatureTable,
} from "./mv-temperature.js";

export const GUN_LIBRARY_VERSION = 3;
export const GUN_LIBRARY_STORAGE_KEY = "abc.gun-library";
export const DEFAULT_GUN_FOLDER_ID = "default";
export const DEFAULT_GUN_FOLDER_NAME = "Default";
export const IMPORTED_GUN_FOLDER_NAME = "Imported";
export const GUN_LIBRARY_EXPORT_FORMAT = "abc-gun-library";
export const GUN_EXPORT_FORMAT = "abc-gun";
export const GUN_TRANSFER_VERSION = 2;

export class GunLibraryPersistenceError extends Error {
  constructor(message = "The gun library could not be written to browser storage.", options = {}) {
    super(message, options);
    this.name = "GunLibraryPersistenceError";
  }
}

const NUMBER_TEXT = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
const TRUE_TEXT = new Set(["true", "on", "yes"]);
const FALSE_TEXT = new Set(["false", "off", "no"]);
const SAVED_GUN_METADATA = new Set([
  "id", "name", "title", "label", "values", "draft", "payload",
  "createdAt", "updatedAt", "version", "configured", "ready", "folderId",
]);

function normalizedName(value, description) {
  const name = String(value ?? "").trim();
  if (!name) throw new TypeError(`${description} needs a name.`);
  return name;
}

function defaultFolder() {
  return { id: DEFAULT_GUN_FOLDER_ID, name: DEFAULT_GUN_FOLDER_NAME };
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (!isRecord(value)) return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, clone(child)]));
}

function normalizedBoolean(value, fieldName) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0" || value === "") return false;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    if (TRUE_TEXT.has(text)) return true;
    if (FALSE_TEXT.has(text)) return false;
  }
  throw new TypeError(`${fieldName} must be a boolean.`);
}

function normalizedNumber(value, fieldName) {
  if (typeof value === "string" && !NUMBER_TEXT.test(value.trim())) {
    throw new TypeError(`${fieldName} must be a finite number.`);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    throw new TypeError(`${fieldName} must be a finite number.`);
  }
  return Object.is(numeric, -0) ? 0 : numeric;
}

function normalizeVelocityBands(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return serializeVelocityBcBands(normalizeVelocityBcBands(parseVelocityBcBands(text)));
}

function normalizeTemperatureVelocityTable(value) {
  return serializeMvTemperatureTable(normalizeMvTemperatureTable(
    parseMvTemperatureTable(String(value ?? "").trim()),
  ));
}

function normalizeFieldDefinitions(options = {}) {
  const definitions = options.fields ?? options.fieldNames ?? [];
  const fieldTypes = new Map();
  const requiredFields = new Set();
  const names = [];

  if (!Array.isArray(definitions)) {
    throw new TypeError("fields must be an array of field names or definitions.");
  }
  for (const definition of definitions) {
    const name = typeof definition === "string" ? definition : definition?.name;
    if (typeof name !== "string" || !name.trim()) {
      throw new TypeError("Each gun field needs a non-empty name.");
    }
    if (fieldTypes.has(name)) continue;
    names.push(name);
    fieldTypes.set(name, typeof definition === "string" ? "" : String(definition.type || ""));
    if (typeof definition !== "string" && definition.required === true) {
      requiredFields.add(name);
    }
  }

  const addTypedNames = (values, type) => {
    if (values === undefined) return;
    if (!Array.isArray(values)) throw new TypeError(`${type} fields must be an array.`);
    for (const name of values) {
      if (typeof name !== "string" || !name) throw new TypeError("Gun field names must be strings.");
      if (!fieldTypes.has(name)) names.push(name);
      fieldTypes.set(name, type);
    }
  };
  addTypedNames(options.numericFields, "number");
  addTypedNames(options.booleanFields, "checkbox");
  if (options.velocityBandFields === undefined) {
    // Recognize the application's conventional field without accidentally
    // turning an otherwise schema-free normalizer into a one-field schema.
    fieldTypes.set("bcb", "velocity-bc-list");
  } else {
    addTypedNames(options.velocityBandFields, "velocity-bc-list");
  }

  return { names, fieldTypes, requiredFields, explicit: names.length > 0 };
}

function normalizerContext(options = {}) {
  const defaults = options.defaults === undefined ? {} : options.defaults;
  if (!isRecord(defaults)) throw new TypeError("Gun defaults must be an object.");
  return { defaults: clone(defaults), ...normalizeFieldDefinitions(options) };
}

function normalizeScalar(value, type, fieldName) {
  if (type === "checkbox" || type === "boolean") {
    return normalizedBoolean(value, fieldName);
  }
  if (type === "number" || type === "number-list") {
    if (type === "number-list") return String(value ?? "").trim();
    return normalizedNumber(value, fieldName);
  }
  if (type === "velocity-bc-list") return normalizeVelocityBands(value);
  if (type === "temperature-velocity-list") return normalizeTemperatureVelocityTable(value);

  if (typeof value === "string") {
    const text = value.trim();
    const lower = text.toLowerCase();
    if (TRUE_TEXT.has(lower) || FALSE_TEXT.has(lower)) {
      return normalizedBoolean(text, fieldName);
    }
    if (NUMBER_TEXT.test(text)) return normalizedNumber(text, fieldName);
    return text.replaceAll("\r\n", "\n").replaceAll("\r", "\n");
  }
  if (typeof value === "number") return normalizedNumber(value, fieldName);
  if (typeof value === "boolean" || value === null) return value;
  if (Array.isArray(value)) return value.map((child) => normalizeScalar(child, "", fieldName));
  if (isRecord(value)) {
    return Object.fromEntries(Object.keys(value).sort().map((key) => (
      [key, normalizeScalar(value[key], "", `${fieldName}.${key}`)]
    )));
  }
  throw new TypeError(`${fieldName} has an unsupported value.`);
}

function migrateLegacyValues(values, context) {
  if (
    !context.fieldTypes.has("mvtb")
    || Object.prototype.hasOwnProperty.call(values, "mvtb")
    || !Object.prototype.hasOwnProperty.call(values, "mv")
  ) {
    return values;
  }
  return {
    ...values,
    mvtb: migrateLegacyMvTemperatureTable(values.mv, values.mvt ?? 59),
  };
}

function normalizeWithContext(values, context) {
  if (!isRecord(values)) throw new TypeError("Gun values must be an object.");
  const source = { ...context.defaults, ...migrateLegacyValues(values, context) };
  const names = context.explicit
    ? context.names
    : Object.keys(source).sort();
  const normalized = {};
  for (const name of names) {
    if (!Object.prototype.hasOwnProperty.call(source, name)) continue;
    normalized[name] = normalizeScalar(source[name], context.fieldTypes.get(name) || "", name);
  }
  return normalized;
}

/**
 * Return the canonical payload used for saved-gun equality. Supplying `fields`
 * also prevents calculator-only values from entering the gun payload.
 */
export function normalizeGunValues(values, options = {}) {
  return normalizeWithContext(values, normalizerContext(options));
}

function stableJsonValue(value) {
  if (Array.isArray(value)) return value.map(stableJsonValue);
  if (!isRecord(value)) return value;
  return Object.fromEntries(Object.keys(value).sort().map((key) => (
    [key, stableJsonValue(value[key])]
  )));
}

/** A collision-free, stable representation suitable for exact-content comparisons. */
export function gunValuesFingerprint(values, options = {}) {
  return JSON.stringify(stableJsonValue(normalizeGunValues(values, options)));
}

export function createGunLibraryState(options = {}) {
  const context = normalizerContext(options);
  return {
    version: GUN_LIBRARY_VERSION,
    configured: false,
    draft: normalizeWithContext({}, context),
    selectedId: null,
    selectedFolderId: DEFAULT_GUN_FOLDER_ID,
    folders: [defaultFolder()],
    guns: [],
  };
}

function oldSavedGunEntries(rawGuns) {
  if (Array.isArray(rawGuns)) return rawGuns.map((gun, index) => [String(index + 1), gun]);
  if (isRecord(rawGuns)) return Object.entries(rawGuns);
  return [];
}

function payloadFromSavedGun(gun) {
  if (!isRecord(gun)) return null;
  if (isRecord(gun.values)) return gun.values;
  if (isRecord(gun.payload)) return gun.payload;
  if (isRecord(gun.draft)) return isRecord(gun.draft.values) ? gun.draft.values : gun.draft;
  return Object.fromEntries(Object.entries(gun).filter(([key]) => !SAVED_GUN_METADATA.has(key)));
}

function configuredFrom(value, fallback = false) {
  if (value === undefined || value === null) return fallback;
  try {
    return normalizedBoolean(value, "configured");
  } catch (_) {
    return fallback;
  }
}

function sanitizeState(raw, context) {
  const empty = {
    version: GUN_LIBRARY_VERSION,
    configured: false,
    draft: normalizeWithContext({}, context),
    selectedId: null,
    selectedFolderId: DEFAULT_GUN_FOLDER_ID,
    folders: [defaultFolder()],
    guns: [],
  };
  if (!isRecord(raw)) return empty;
  if (raw.version !== undefined) {
    const storedVersion = Number(raw.version);
    if (!Number.isInteger(storedVersion) || storedVersion < 0 || storedVersion > GUN_LIBRARY_VERSION) {
      return empty;
    }
  }

  const rawDraft = isRecord(raw.draft?.values)
    ? raw.draft.values
    : isRecord(raw.draft)
      ? raw.draft
      : isRecord(raw.values)
        ? raw.values
        : {};
  let draftWasValid = true;
  try {
    empty.draft = normalizeWithContext(rawDraft, context);
  } catch (_) {
    // A damaged draft must not prevent access to otherwise valid saved guns.
    draftWasValid = false;
  }

  const usedFolderIds = new Set([DEFAULT_GUN_FOLDER_ID]);
  const folderIdAliases = new Map([[DEFAULT_GUN_FOLDER_ID, DEFAULT_GUN_FOLDER_ID]]);
  if (Array.isArray(raw.folders)) {
    for (const folder of raw.folders) {
      if (!isRecord(folder)) continue;
      try {
        const sourceId = String(folder.id ?? "").trim();
        const name = normalizedName(folder.name, "A gun folder");
        if (!sourceId || sourceId === DEFAULT_GUN_FOLDER_ID) {
          if (sourceId === DEFAULT_GUN_FOLDER_ID) {
            folderIdAliases.set(sourceId, DEFAULT_GUN_FOLDER_ID);
          }
          continue;
        }
        let id = sourceId;
        let suffix = 2;
        while (usedFolderIds.has(id)) id = `${sourceId}-${suffix++}`;
        usedFolderIds.add(id);
        if (!folderIdAliases.has(sourceId)) folderIdAliases.set(sourceId, id);
        empty.folders.push({ id, name });
      } catch (_) {
        // Preserve other folders when a stored folder entry is malformed.
      }
    }
  }

  const usedIds = new Set();
  const rawGuns = raw.guns ?? raw.savedGuns ?? raw.saved;
  for (const [legacyKey, gun] of oldSavedGunEntries(rawGuns)) {
    const payload = payloadFromSavedGun(gun);
    if (!payload) continue;
    try {
      const baseId = String(gun.id ?? legacyKey ?? "gun").trim() || "gun";
      let id = baseId;
      let suffix = 2;
      while (usedIds.has(id)) id = `${baseId}-${suffix++}`;
      usedIds.add(id);
      const fallbackName = `Gun ${empty.guns.length + 1}`;
      const name = String(gun.name ?? gun.title ?? gun.label ?? fallbackName).trim() || fallbackName;
      const requestedFolderId = String(gun.folderId ?? DEFAULT_GUN_FOLDER_ID);
      const folderId = folderIdAliases.get(requestedFolderId) ?? DEFAULT_GUN_FOLDER_ID;
      empty.guns.push({ id, folderId, name, values: normalizeWithContext(payload, context) });
    } catch (_) {
      // Drop only the malformed saved entry and preserve the rest of the library.
    }
  }

  const requestedSelectedId = raw.selectedId ?? raw.selectedGunId ?? raw.loadedGunId ?? null;
  const selectedId = requestedSelectedId === null ? null : String(requestedSelectedId);
  empty.selectedId = empty.guns.some((gun) => gun.id === selectedId) ? selectedId : null;
  const requestedFolderId = String(raw.selectedFolderId ?? "");
  const selectedGunFolderId = empty.guns.find((gun) => gun.id === empty.selectedId)?.folderId;
  empty.selectedFolderId = folderIdAliases.get(requestedFolderId)
    ?? selectedGunFolderId
    ?? DEFAULT_GUN_FOLDER_ID;
  empty.configured = draftWasValid && configuredFrom(
    raw.configured ?? raw.ready ?? raw.draft?.configured ?? raw.draft?.ready,
    empty.selectedId !== null,
  );
  return empty;
}

/** Parse current or legacy storage data without throwing on damaged browser state. */
export function parseGunLibraryState(serialized, options = {}) {
  const context = normalizerContext(options);
  try {
    const raw = typeof serialized === "string" ? JSON.parse(serialized) : serialized;
    return sanitizeState(raw, context);
  } catch (_) {
    return sanitizeState(null, context);
  }
}

/** Validate and serialize a state without mutating it. */
export function serializeGunLibraryState(state, options = {}) {
  return JSON.stringify(sanitizeState(state, normalizerContext(options)));
}

function transferRecord(serialized, description) {
  let value;
  try {
    value = typeof serialized === "string" ? JSON.parse(serialized) : serialized;
  } catch (cause) {
    throw new TypeError(`${description} is not valid JSON.`, { cause });
  }
  if (!isRecord(value)) throw new TypeError(`${description} must be a JSON object.`);
  return value;
}

function transferText(value) {
  return `${JSON.stringify(stableJsonValue(value), null, 2)}\n`;
}

function compareText(left, right) {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function validateTransferHeader(raw, format, description) {
  if (raw.format !== format) throw new TypeError(`${description} has an unrecognized format.`);
  const version = raw.version;
  if (!Number.isInteger(version) || version < 1 || version > GUN_TRANSFER_VERSION) {
    throw new TypeError(`${description} uses an unsupported version.`);
  }
  return version;
}

function normalizeTransferGunValues(values, context, transferVersion) {
  if (!isRecord(values)) throw new TypeError("Imported gun values must be an object.");
  const source = transferVersion === 1 ? migrateLegacyValues(values, context) : values;
  if (transferVersion === GUN_TRANSFER_VERSION) {
    for (const fieldName of context.names) {
      if (!Object.prototype.hasOwnProperty.call(source, fieldName)) {
        throw new TypeError(`Imported gun values are missing required field “${fieldName}”.`);
      }
    }
  }
  if (
    context.fieldTypes.has("mvtb")
    && !Object.prototype.hasOwnProperty.call(source, "mvtb")
  ) {
    throw new TypeError("Imported gun values are missing required field “mvtb”.");
  }
  return normalizeWithContext(source, context);
}

/** Produce a deterministic, portable representation of every saved gun and folder. */
export function serializeGunLibraryExport(state, options = {}) {
  const sanitized = sanitizeState(state, normalizerContext(options));
  const folders = [...sanitized.folders]
    .sort((left, right) => compareText(left.name, right.name) || compareText(left.id, right.id));
  const guns = [...sanitized.guns]
    .sort((left, right) => (
      compareText(left.folderId, right.folderId)
      || compareText(left.name, right.name)
      || compareText(left.id, right.id)
    ));
  return transferText({
    format: GUN_LIBRARY_EXPORT_FORMAT,
    version: GUN_TRANSFER_VERSION,
    selectedFolderId: sanitized.selectedFolderId,
    selectedId: sanitized.selectedId,
    folders,
    guns,
  });
}

/** Strictly validate a whole-library transfer. No damaged entry is silently dropped. */
export function parseGunLibraryExport(serialized, options = {}) {
  const raw = transferRecord(serialized, "The gun library import");
  const transferVersion = validateTransferHeader(
    raw,
    GUN_LIBRARY_EXPORT_FORMAT,
    "The gun library import",
  );
  if (!Array.isArray(raw.folders) || !Array.isArray(raw.guns)) {
    throw new TypeError("The gun library import must contain folder and gun lists.");
  }
  const context = normalizerContext(options);
  const folderIds = new Set();
  const folders = raw.folders.map((folder) => {
    if (!isRecord(folder)) throw new TypeError("Each imported gun folder must be an object.");
    const id = String(folder.id ?? "").trim();
    if (!id) throw new TypeError("Each imported gun folder needs an id.");
    if (folderIds.has(id)) throw new TypeError(`The imported folder id “${id}” is duplicated.`);
    folderIds.add(id);
    return { id, name: normalizedName(folder.name, "An imported gun folder") };
  });
  const gunIds = new Set();
  const guns = raw.guns.map((gun) => {
    if (!isRecord(gun)) throw new TypeError("Each imported gun must be an object.");
    const id = String(gun.id ?? "").trim();
    if (!id) throw new TypeError("Each imported gun needs an id.");
    if (gunIds.has(id)) throw new TypeError(`The imported gun id “${id}” is duplicated.`);
    gunIds.add(id);
    const folderId = String(gun.folderId ?? "").trim();
    if (!folderIds.has(folderId)) {
      throw new TypeError(`Imported gun “${id}” refers to an unknown folder.`);
    }
    return {
      id,
      folderId,
      name: normalizedName(gun.name, "An imported gun"),
      values: normalizeTransferGunValues(gun.values, context, transferVersion),
    };
  });
  return { folders, guns };
}

/** Produce a deterministic, portable representation of one saved gun. */
export function serializeGunExport(gun, options = {}) {
  if (!isRecord(gun)) throw new TypeError("A gun export needs a saved gun.");
  const context = normalizerContext(options);
  const id = String(gun.id ?? "").trim();
  return transferText({
    format: GUN_EXPORT_FORMAT,
    version: GUN_TRANSFER_VERSION,
    gun: {
      ...(id ? { id } : {}),
      name: normalizedName(gun.name, "A gun export"),
      values: normalizeWithContext(gun.values, context),
    },
  });
}

/** Strictly validate a single-gun transfer. */
export function parseGunExport(serialized, options = {}) {
  const raw = transferRecord(serialized, "The gun import");
  const transferVersion = validateTransferHeader(raw, GUN_EXPORT_FORMAT, "The gun import");
  if (!isRecord(raw.gun)) throw new TypeError("The gun import must contain one gun.");
  const context = normalizerContext(options);
  const id = String(raw.gun.id ?? "").trim();
  return {
    ...(id ? { id } : {}),
    name: normalizedName(raw.gun.name, "An imported gun"),
    values: normalizeTransferGunValues(raw.gun.values, context, transferVersion),
  };
}

function defaultStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch (_) {
    return null;
  }
}

let generatedIdCounter = 0;
function defaultIdFactory() {
  try {
    if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  } catch (_) {
    // Fall through for browsers that expose an inaccessible crypto implementation.
  }
  generatedIdCounter += 1;
  return `gun-${Date.now().toString(36)}-${generatedIdCounter.toString(36)}`;
}

function storageText(storage, key) {
  try {
    return storage?.getItem?.(key) ?? null;
  } catch (_) {
    return null;
  }
}

function writeStorage(storage, key, state) {
  if (typeof storage?.setItem !== "function") {
    return { written: false, cause: new Error("Browser storage is unavailable.") };
  }
  try {
    storage.setItem(key, JSON.stringify(state));
    return { written: true, cause: null };
  } catch (cause) {
    return { written: false, cause };
  }
}

/**
 * Create a stateful browser-storage facade. All returned snapshots are copies;
 * the only side effect is writing the configured storage key after a mutation.
 */
export function createGunLibrary(options = {}) {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const storageKey = options.storageKey || GUN_LIBRARY_STORAGE_KEY;
  const idFactory = options.idFactory || defaultIdFactory;
  if (typeof idFactory !== "function") throw new TypeError("idFactory must be a function.");

  const context = normalizerContext(options);
  const normalizationOptions = {
    fields: context.names.map((name) => ({
      name,
      type: context.fieldTypes.get(name),
      required: context.requiredFields.has(name),
    })),
    defaults: context.defaults,
    velocityBandFields: [],
  };
  let state = parseGunLibraryState(storageText(storage, storageKey), normalizationOptions);
  const listeners = new Set();

  const snapshot = () => clone(state);
  const commit = (nextState) => {
    const candidate = sanitizeState(nextState, context);
    const persistence = writeStorage(storage, storageKey, candidate);
    if (!persistence.written) {
      throw new GunLibraryPersistenceError(undefined, { cause: persistence.cause });
    }
    state = candidate;
    const current = snapshot();
    for (const listener of listeners) listener(current);
    return current;
  };
  const fingerprint = (values) => JSON.stringify(stableJsonValue(normalizeWithContext(values, context)));
  const selectedGun = () => state.guns.find((gun) => gun.id === state.selectedId) || null;
  const matchingGun = (values = state.draft) => {
    const target = fingerprint(values);
    const matches = state.guns.filter((gun) => fingerprint(gun.values) === target);
    return matches.find((gun) => gun.id === state.selectedId) || matches[0] || null;
  };
  const uniqueGunId = (requested, guns = state.guns) => {
    const base = String(requested || idFactory()).trim();
    if (!base) throw new TypeError("A generated gun id cannot be empty.");
    let id = base;
    let suffix = 2;
    while (guns.some((gun) => gun.id === id)) id = `${base}-${suffix++}`;
    return id;
  };
  const uniqueFolderId = (requested, folders = state.folders) => {
    const base = String(requested || "folder").trim();
    if (!base) throw new TypeError("A gun folder id cannot be empty.");
    let id = base;
    let suffix = 2;
    while (folders.some((folder) => folder.id === id)) id = `${base}-${suffix++}`;
    return id;
  };
  const upsertGun = (guns, folderId, importedGun) => {
    const target = guns.find((gun) => gun.folderId === folderId && gun.name === importedGun.name);
    const id = target?.id || uniqueGunId(importedGun.id, guns);
    const gun = { id, folderId, name: importedGun.name, values: importedGun.values };
    return {
      gun,
      guns: target
        ? guns.map((candidate) => candidate.id === target.id ? gun : candidate)
        : [...guns, gun],
    };
  };

  return Object.freeze({
    storageKey,
    getState: snapshot,
    reload() {
      state = parseGunLibraryState(storageText(storage, storageKey), normalizationOptions);
      const current = snapshot();
      for (const listener of listeners) listener(current);
      return current;
    },
    updateDraft(patch, { configured = state.configured } = {}) {
      if (!isRecord(patch)) throw new TypeError("Gun draft updates must be an object.");
      return commit({
        ...state,
        configured: normalizedBoolean(configured, "configured"),
        draft: normalizeWithContext({ ...state.draft, ...patch }, context),
      });
    },
    setDraft(values, { configured = state.configured } = {}) {
      return commit({
        ...state,
        configured: normalizedBoolean(configured, "configured"),
        draft: normalizeWithContext(values, context),
      });
    },
    configureDraft(values = state.draft) {
      return commit({ ...state, configured: true, draft: normalizeWithContext(values, context) });
    },
    setConfigured(configured) {
      return commit({ ...state, configured: normalizedBoolean(configured, "configured") });
    },
    saveGun(name, saveOptions = {}) {
      if (!isRecord(saveOptions)) throw new TypeError("Save options must be an object.");
      const values = normalizeWithContext(saveOptions.values ?? state.draft, context);
      const explicitId = saveOptions.id === undefined ? null : String(saveOptions.id);
      const folderId = saveOptions.folderId === undefined
        ? state.selectedFolderId : String(saveOptions.folderId);
      if (!state.folders.some((folder) => folder.id === folderId)) {
        throw new TypeError("Select a valid destination folder before saving this gun.");
      }
      const selected = selectedGun();
      const gunName = normalizedName(name ?? selected?.name, "A saved gun");
      const target = state.guns.find((gun) => (
        gun.folderId === folderId && gun.name === gunName
      ));
      const id = target?.id || uniqueGunId(explicitId);
      const gun = { id, folderId, name: gunName, values };
      const guns = target
        ? state.guns.map((candidate) => candidate.id === id ? gun : candidate)
        : [...state.guns, gun];
      commit({
        ...state,
        configured: true,
        draft: values,
        selectedId: id,
        selectedFolderId: folderId,
        guns,
      });
      return clone(gun);
    },
    loadGun(id) {
      const gun = state.guns.find((candidate) => candidate.id === String(id));
      if (!gun) return null;
      return commit({
        ...state,
        configured: true,
        draft: gun.values,
        selectedId: gun.id,
        selectedFolderId: gun.folderId,
      });
    },
    deleteGun(id) {
      const normalizedId = String(id);
      if (!state.guns.some((gun) => gun.id === normalizedId)) return false;
      commit({
        ...state,
        selectedId: state.selectedId === normalizedId ? null : state.selectedId,
        guns: state.guns.filter((gun) => gun.id !== normalizedId),
      });
      return true;
    },
    resetDraft() {
      return commit({
        ...state,
        configured: false,
        draft: normalizeWithContext({}, context),
        selectedId: null,
      });
    },
    getFolders() {
      return clone(state.folders);
    },
    getGuns(folderId = state.selectedFolderId) {
      const normalizedId = String(folderId);
      return clone(state.guns.filter((gun) => gun.folderId === normalizedId));
    },
    createFolder(name, folderOptions = {}) {
      if (!isRecord(folderOptions)) throw new TypeError("Folder options must be an object.");
      const folderName = normalizedName(name, "A gun folder");
      const existing = state.folders.find((folder) => folder.name === folderName);
      if (existing) {
        if (state.selectedFolderId !== existing.id) {
          commit({ ...state, selectedFolderId: existing.id, selectedId: null });
        }
        return clone(existing);
      }
      const folder = {
        id: uniqueFolderId(folderOptions.id ?? folderName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")),
        name: folderName,
      };
      commit({
        ...state,
        selectedFolderId: folder.id,
        selectedId: null,
        folders: [...state.folders, folder],
      });
      return clone(folder);
    },
    selectFolder(id) {
      const folder = state.folders.find((candidate) => candidate.id === String(id));
      if (!folder) return null;
      commit({ ...state, selectedFolderId: folder.id, selectedId: null });
      return clone(folder);
    },
    moveGun(id, folderId) {
      const gunId = String(id);
      const destinationId = String(folderId);
      const gun = state.guns.find((candidate) => candidate.id === gunId);
      if (!gun || !state.folders.some((folder) => folder.id === destinationId)) return null;
      const collision = state.guns.find((candidate) => (
        candidate.id !== gunId
        && candidate.folderId === destinationId
        && candidate.name === gun.name
      ));
      const moved = { ...gun, folderId: destinationId };
      const guns = state.guns
        .filter((candidate) => candidate.id !== collision?.id)
        .map((candidate) => candidate.id === gunId ? moved : candidate);
      commit({
        ...state,
        selectedId: gunId,
        selectedFolderId: destinationId,
        guns,
      });
      return clone(moved);
    },
    exportLibrary() {
      return serializeGunLibraryExport(state, normalizationOptions);
    },
    exportGun(id = state.selectedId) {
      const gun = state.guns.find((candidate) => candidate.id === String(id));
      if (!gun) throw new TypeError("Select a saved gun to export.");
      return serializeGunExport(gun, normalizationOptions);
    },
    importLibrary(serialized) {
      const imported = parseGunLibraryExport(serialized, normalizationOptions);
      let folders = state.folders;
      let importedFolder = folders.find((folder) => folder.name === IMPORTED_GUN_FOLDER_NAME);
      if (!importedFolder) {
        importedFolder = {
          id: uniqueFolderId("imported", folders),
          name: IMPORTED_GUN_FOLDER_NAME,
        };
        folders = [...folders, importedFolder];
      }
      let guns = state.guns;
      const importedGuns = [];
      for (const importedGun of imported.guns) {
        const result = upsertGun(guns, importedFolder.id, importedGun);
        guns = result.guns;
        importedGuns.push(result.gun);
      }
      commit({
        ...state,
        selectedId: null,
        selectedFolderId: importedFolder.id,
        folders,
        guns,
      });
      return clone({ folder: importedFolder, guns: importedGuns });
    },
    importGun(serialized) {
      const imported = parseGunExport(serialized, normalizationOptions);
      const result = upsertGun(state.guns, state.selectedFolderId, imported);
      commit({ ...state, guns: result.guns });
      return clone(result.gun);
    },
    getSelectedGun() {
      return clone(selectedGun());
    },
    getMatchingGun(values = state.draft) {
      return clone(matchingGun(values));
    },
    getDraftFingerprint() {
      return fingerprint(state.draft);
    },
    getStatus() {
      const selected = selectedGun();
      const match = matchingGun();
      return {
        configured: state.configured,
        fingerprint: fingerprint(state.draft),
        selectedGun: clone(selected),
        matchingGun: clone(match),
        modified: Boolean(selected && fingerprint(selected.values) !== fingerprint(state.draft)),
      };
    },
    subscribe(listener) {
      if (typeof listener !== "function") throw new TypeError("A subscriber must be a function.");
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  });
}

/** Explicitly named convenience for application code that wants window.localStorage. */
export function createLocalStorageGunLibrary(options = {}) {
  return createGunLibrary({ ...options, storage: options.storage ?? defaultStorage() });
}
